import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Bot,
  Send,
  Handshake,
  Calculator,
  Leaf,
  Megaphone,
  Scale,
  Crown,
  Wrench,
  Users,
  Truck,
  ChevronLeft,
  MessageSquare,
  Sparkles,
  Loader2,
  Plus,
  Settings,
  Mic,
  MicOff,
  Image,
  RotateCcw,
  Check,
  Volume2,
  Paperclip,
  X,
  FileText,
  FileSpreadsheet,
  Presentation,
  FileDown,
  Film,
  Music,
  Link,
  Camera,
  Phone,
  StopCircle,
  Download,
  UserMinus,
  Search,
  Pencil,
} from 'lucide-react';
import { DocumentGenerator, DocumentDownloadButton } from '@/components/DocumentGenerator';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useNavigate, useLocation } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs pour les graphiques
const CHART_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Composant qui rend les graphiques [GRAPHIQUE:type:titre:données] dans les messages
const MessageRenderer = ({ content, agentColor }) => {
  if (!content) return null;

  const graphRegex = /\[GRAPHIQUE:(bar|pie|line|donut):([^:]+):([^\]]+)\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = graphRegex.exec(content)) !== null) {
    // Texte avant le graphique
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }
    // Parser les données du graphique
    const chartType = match[1];
    const chartTitle = match[2].trim();
    const dataStr = match[3].trim();
    const dataEntries = dataStr.split(',').map(entry => {
      const [label, val] = entry.split('=').map(s => s.trim());
      return { name: label, value: parseFloat(val) || 0 };
    }).filter(d => d.name);

    parts.push({ type: 'chart', chartType, chartTitle, data: dataEntries });
    lastIndex = match.index + match[0].length;
  }

  // Texte restant après le dernier graphique
  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) });
  }

  // Pas de graphique trouvé → rendu texte simple
  if (parts.length === 0 || (parts.length === 1 && parts[0].type === 'text')) {
    return <p className="whitespace-pre-wrap">{content}</p>;
  }

  return (
    <div className="space-y-4">
      {parts.map((part, i) => {
        if (part.type === 'text') {
          return <p key={i} className="whitespace-pre-wrap">{part.value}</p>;
        }
        // Rendu graphique
        return (
          <div key={i} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 my-3" data-testid={`chart-${i}`}>
            <h4 className="text-sm font-semibold text-zinc-200 mb-3 text-center">{part.chartTitle}</h4>
            <ResponsiveContainer width="100%" height={220}>
              {part.chartType === 'bar' ? (
                <BarChart data={part.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #333', borderRadius: '8px', color: '#e5e7eb' }} />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {part.data.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              ) : part.chartType === 'line' ? (
                <LineChart data={part.data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #333', borderRadius: '8px', color: '#e5e7eb' }} />
                  <Line type="monotone" dataKey="value" stroke={agentColor || '#10b981'} strokeWidth={2} dot={{ fill: agentColor || '#10b981', r: 4 }} />
                </LineChart>
              ) : (
                <PieChart>
                  <Pie data={part.data} cx="50%" cy="50%" innerRadius={part.chartType === 'donut' ? 50 : 0} outerRadius={80}
                    paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#666' }}>
                    {part.data.map((_, idx) => (
                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #333', borderRadius: '8px', color: '#e5e7eb' }} />
                  <Legend wrapperStyle={{ fontSize: 11, color: '#9ca3af' }} />
                </PieChart>
              )}
            </ResponsiveContainer>
          </div>
        );
      })}
    </div>
  );
};

// Composant Saisie Vocale
const VoiceRecorder = ({ onTranscription, disabled }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [permissionStatus, setPermissionStatus] = useState('prompt'); // 'granted', 'denied', 'prompt'
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Vérifier les permissions au montage
  useEffect(() => {
    const checkPermission = async () => {
      try {
        if (navigator.permissions) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          setPermissionStatus(result.state);
          result.onchange = () => setPermissionStatus(result.state);
        }
      } catch (e) {
        console.log('Permission API non supportée');
      }
    };
    checkPermission();
  }, []);

  const requestPermission = async () => {
    try {
      // Demander explicitement l'autorisation
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setPermissionStatus('granted');
      toast.success("🎙️ Autorisation accordée ! Vous pouvez maintenant enregistrer.");
      return true;
    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setPermissionStatus('denied');
        toast.error("❌ Accès au microphone refusé. Veuillez autoriser l'accès dans les paramètres de votre navigateur.", {
          duration: 5000,
          description: "Cliquez sur l'icône cadenas dans la barre d'adresse pour modifier les permissions."
        });
      } else if (error.name === 'NotFoundError') {
        toast.error("🎤 Aucun microphone détecté sur cet appareil.");
      } else {
        toast.error(`Erreur: ${error.message}`);
      }
      return false;
    }
  };

  const startRecording = async () => {
    // Si permission pas encore accordée, la demander d'abord
    if (permissionStatus !== 'granted') {
      const granted = await requestPermission();
      if (!granted) return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      });
      
      // Vérifier les codecs supportés
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';
      
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(blob);
      };

      mediaRecorderRef.current.start(1000); // Enregistrer par chunks de 1 seconde
      setIsRecording(true);
      setRecordingTime(0);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success("🎙️ Enregistrement démarré - Parlez !");
    } catch (error) {
      console.error('Erreur micro:', error);
      if (error.name === 'NotAllowedError') {
        setPermissionStatus('denied');
        toast.error("❌ Accès au microphone refusé. Cliquez sur l'icône cadenas dans la barre d'adresse.", {
          duration: 5000
        });
      } else {
        toast.error(`Impossible d'accéder au microphone: ${error.message}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const transcribeAudio = async (blob) => {
    setIsTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      
      const token = localStorage.getItem('token');
      
      // Utiliser l'endpoint qui upload ET transcrit en une seule étape
      const response = await fetch(`${API_URL}/api/integrations/transcribe/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success && data.text) {
        onTranscription(data.text);
        toast.success("✅ Transcription terminée !");
      } else if (data.detail) {
        toast.error(`Erreur: ${data.detail}`);
      } else {
        // Fallback: uploader comme fichier normal
        const uploadRes = await fetch(`${API_URL}/api/brain/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        const uploadData = await uploadRes.json();
        if (uploadData.success) {
          onTranscription("[🎙️ Message vocal]", uploadData.file.id);
          toast.info("Audio envoyé à l'assistant");
        }
      }
    } catch (error) {
      console.error('Erreur transcription:', error);
      toast.error("Erreur de transcription");
    } finally {
      setIsTranscribing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-2">
      {isRecording ? (
        <>
          <Badge variant="destructive" className="animate-pulse">
            <span className="w-2 h-2 bg-white rounded-full mr-2 animate-ping" />
            {formatTime(recordingTime)}
          </Badge>
          <Button
            size="icon"
            variant="destructive"
            onClick={stopRecording}
            className="rounded-full"
          >
            <StopCircle className="w-5 h-5" />
          </Button>
        </>
      ) : isTranscribing ? (
        <Button size="icon" variant="outline" disabled className="rounded-full">
          <Loader2 className="w-5 h-5 animate-spin" />
        </Button>
      ) : permissionStatus === 'denied' ? (
        <Button
          size="icon"
          variant="outline"
          onClick={requestPermission}
          disabled={disabled}
          className="rounded-full border-red-300 text-red-500 hover:bg-red-50"
          title="Micro bloqué - Cliquez pour réessayer"
        >
          <MicOff className="w-5 h-5" />
        </Button>
      ) : (
        <Button
          size="icon"
          variant="outline"
          onClick={startRecording}
          disabled={disabled}
          className={`rounded-full transition-colors ${
            permissionStatus === 'granted' 
              ? 'hover:bg-primary hover:text-primary-foreground' 
              : 'hover:bg-amber-50 border-amber-300'
          }`}
          title={permissionStatus === 'granted' ? "Enregistrer un message vocal" : "Cliquez pour autoriser le microphone"}
        >
          <Mic className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
};

// Icon mapping
const ICONS = {
  bot: Bot,
  handshake: Handshake,
  calculator: Calculator,
  leaf: Leaf,
  megaphone: Megaphone,
  scale: Scale,
  crown: Crown,
  phone: Phone,
  wrench: Wrench,
  users: Users,
  truck: Truck,
};

// Agent Customization Dialog
const AgentCustomizationDialog = ({ agent, voices, onSave, onReset }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [customName, setCustomName] = useState(agent.nom);
  const [customVoice, setCustomVoice] = useState(agent.voice || 'nova');
  const [customAvatarUrl, setCustomAvatarUrl] = useState(agent.avatar_url || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewVoice, setPreviewVoice] = useState(null);
  const fileInputRef = useRef(null);

  // Fonction pour uploader une image
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop grande (max 5MB)');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/brain/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success && data.file?.url) {
        setCustomAvatarUrl(data.file.url);
        toast.success('Photo téléchargée !');
      } else {
        // Fallback: convertir en base64
        const reader = new FileReader();
        reader.onload = (e) => {
          setCustomAvatarUrl(e.target.result);
          toast.success('Photo chargée en local');
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      // Fallback base64
      const reader = new FileReader();
      reader.onload = (e) => {
        setCustomAvatarUrl(e.target.result);
        toast.info('Photo chargée localement');
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };
  
  // Prévisualiser la voix
  const previewVoiceSound = async (voiceId) => {
    if (previewVoice) return; // Déjà en cours
    
    setPreviewVoice(voiceId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify({
          text: `Bonjour, je suis ${customName || agent.nom_defaut}. Comment puis-je vous aider aujourd'hui ?`,
          voice: voiceId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.audio_base64) {
          const audioData = atob(data.audio_base64);
          const audioArray = new Uint8Array(audioData.length);
          for (let i = 0; i < audioData.length; i++) {
            audioArray[i] = audioData.charCodeAt(i);
          }
          const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
          const audioUrl = URL.createObjectURL(audioBlob);
          const audio = new Audio(audioUrl);
          audio.onended = () => {
            setPreviewVoice(null);
            URL.revokeObjectURL(audioUrl);
          };
          await audio.play();
        }
      }
    } catch (error) {
      console.error('Erreur preview:', error);
    } finally {
      setTimeout(() => setPreviewVoice(null), 5000);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      agent_id: agent.id,
      custom_name: customName !== agent.nom_defaut ? customName : null,
      custom_avatar_url: customAvatarUrl !== agent.avatar_url_defaut ? customAvatarUrl : null,
      custom_voice: customVoice,
      is_enabled: true
    });
    setSaving(false);
    setOpen(false);
  };

  const handleReset = async () => {
    await onReset(agent.id);
    setCustomName(agent.nom_defaut);
    setCustomVoice('nova');
    setCustomAvatarUrl(agent.avatar_url_defaut);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <Settings className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Personnaliser {agent.role || agent.nom_defaut}</DialogTitle>
          <DialogDescription>
            Choisissez le nom, l'avatar et la voix de cet assistant
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Avatar preview avec bouton upload */}
          <div className="flex items-center gap-4">
            <div className="relative">
              {customAvatarUrl ? (
                <img 
                  src={customAvatarUrl} 
                  alt={customName}
                  className="w-20 h-20 rounded-full object-cover border-2 cursor-pointer hover:opacity-80 transition-opacity"
                  style={{ borderColor: agent.color }}
                  onClick={() => fileInputRef.current?.click()}
                />
              ) : (
                <div 
                  className="w-20 h-20 rounded-full flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                  style={{ backgroundColor: `${agent.color}20` }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {(() => { const Icon = ICONS[agent.icon] || Bot; return <Icon className="w-10 h-10" style={{ color: agent.color }} />; })()}
                </div>
              )}
              <button
                className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
            </div>
            <div>
              <p className="font-semibold">{customName}</p>
              <p className="text-sm text-muted-foreground">{agent.role || agent.description}</p>
              <p className="text-xs text-muted-foreground mt-1">Cliquez sur l'image pour changer</p>
            </div>
          </div>

          {/* Nom personnalisé */}
          <div className="space-y-2">
            <Label>Nom de l'assistant</Label>
            <Input
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder={agent.nom_defaut}
            />
          </div>

          {/* URL Avatar (optionnel) */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Ou coller une URL d'image</Label>
            <div className="flex gap-2">
              <Input
                value={customAvatarUrl?.startsWith('data:') ? '' : customAvatarUrl}
                onChange={(e) => setCustomAvatarUrl(e.target.value)}
                placeholder="https://..."
                className="text-xs"
              />
            </div>
          </div>

          {/* Sélection voix */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Volume2 className="w-4 h-4" />
              Voix de l'assistant
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {voices.map((voice) => (
                <button
                  key={voice.id}
                  onClick={() => setCustomVoice(voice.id)}
                  className={`p-3 rounded-lg border text-left transition-all relative ${
                    customVoice === voice.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-muted-foreground/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{voice.name}</span>
                    <div className="flex items-center gap-1">
                      {/* Bouton écouter */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          previewVoiceSound(voice.id);
                        }}
                        className="p-1 hover:bg-muted rounded"
                        disabled={previewVoice}
                      >
                        {previewVoice === voice.id ? (
                          <Loader2 className="w-3 h-3 animate-spin text-primary" />
                        ) : (
                          <Volume2 className="w-3 h-3 text-muted-foreground hover:text-primary" />
                        )}
                      </button>
                      {customVoice === voice.id && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{voice.description}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            className="sm:mr-auto gap-2"
            onClick={() => {
              setOpen(false);
              navigate('/agents/config', { state: { selectedAgent: agent.id } });
            }}
          >
            <Sparkles className="w-4 h-4" />
            Config. avancée
          </Button>
          {agent.is_customized && (
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="w-4 h-4 mr-2" />
              Réinitialiser
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AgentsPage = () => {
  const { activeSociete } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const preselectedAgentId = location.state?.preselectedAgentId;
  const [agents, setAgents] = useState([]);
  const [voices, setVoices] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachedUrls, setAttachedUrls] = useState([]);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false); // Mode conversation vocale
  const [autoSpeak, setAutoSpeak] = useState(true); // Réponse vocale auto
  const [isListening, setIsListening] = useState(false); // En écoute
  const [isSpeaking, setIsSpeaking] = useState(false); // En train de parler
  const [collaboratorEmails, setCollaboratorEmails] = useState({}); // Cache email des collaborateurs
  const [searchConvQuery, setSearchConvQuery] = useState('');
  const [editingTitleConvId, setEditingTitleConvId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  
  // Helper pour obtenir les headers avec cloisonnement workspace
  const getHeaders = useCallback(() => {
    const headers = {
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json',
      'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
    };
    if (activeSociete?.id) {
      headers['X-Societe-Id'] = activeSociete.id;
    }
    return headers;
  }, [activeSociete]);

  // Fetch agents on mount
  useEffect(() => {
    fetchAgents();
    fetchVoices();
  }, []);

  // Pré-sélectionner l'agent si preselectedAgentId est fourni
  useEffect(() => {
    if (preselectedAgentId && agents.length > 0 && !selectedAgent) {
      const agentToSelect = agents.find(a => a.id === preselectedAgentId);
      if (agentToSelect) {
        setSelectedAgent(agentToSelect);
        // Nettoyer le state pour éviter la re-sélection
        window.history.replaceState({}, document.title);
      }
    }
  }, [preselectedAgentId, agents, selectedAgent]);

  // Fetch conversations when agent changes
  useEffect(() => {
    if (selectedAgent) {
      fetchConversations(selectedAgent.id);
    }
  }, [selectedAgent]);

  // Debounce search: refetch quand la recherche change
  useEffect(() => {
    if (!selectedAgent) return;
    const timer = setTimeout(() => {
      fetchConversations(selectedAgent.id);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchConvQuery]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchAgents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/brain/agents`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Erreur chargement agents:', error);
      toast.error('Erreur de chargement des agents');
    }
  };

  const fetchVoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/brain/voices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVoices(data.voices || []);
    } catch (error) {
      console.error('Erreur chargement voix:', error);
    }
  };

  const saveAgentCustomization = async (customization) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/brain/agents/${customization.agent_id}/customize`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify(customization)
      });
      
      if (response.ok) {
        toast.success('Personnalisation enregistrée');
        fetchAgents(); // Recharger les agents
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    }
  };

  const resetAgentCustomization = async (agentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/brain/agents/${agentId}/customize`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Personnalisation réinitialisée');
        fetchAgents();
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // État pour le TTS
  const [playingTTS, setPlayingTTS] = useState(null); // ID du message en cours de lecture
  const [audioElement, setAudioElement] = useState(null);

  // Fonction pour lire un message avec TTS
  const playTTS = async (messageContent, messageIndex) => {
    // Si déjà en lecture, arrêter
    if (playingTTS === messageIndex && audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setPlayingTTS(null);
      setAudioElement(null);
      return;
    }

    // Arrêter toute lecture précédente
    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
    }

    setPlayingTTS(messageIndex);

    try {
      const token = localStorage.getItem('token');
      
      // Déterminer la voix à utiliser
      const voice = selectedAgent?.voice || selectedAgent?.voix || 'nova';
      
      const response = await fetch(`${API_URL}/api/integrations/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify({
          text: messageContent,
          voice: voice
        })
      });

      if (!response.ok) {
        throw new Error('Erreur TTS');
      }

      const data = await response.json();
      
      if (!data.success || !data.audio_base64) {
        throw new Error('Pas de données audio');
      }
      
      // Convertir base64 en blob audio
      const audioData = atob(data.audio_base64);
      const audioArray = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioArray[i] = audioData.charCodeAt(i);
      }
      const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      // Créer et jouer l'audio
      const audio = new Audio(audioUrl);
      setAudioElement(audio);
      
      audio.onended = () => {
        setPlayingTTS(null);
        setAudioElement(null);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setPlayingTTS(null);
        setAudioElement(null);
        toast.error('Erreur de lecture audio');
      };
      
      await audio.play();
      toast.success(`🔊 ${selectedAgent.nom} parle...`);
      
    } catch (error) {
      console.error('Erreur TTS:', error);
      toast.error('Impossible de lire ce message');
      setPlayingTTS(null);
      setAudioElement(null);
    }
  };

  // Nettoyer l'audio quand on change d'agent
  useEffect(() => {
    return () => {
      if (audioElement) {
        audioElement.pause();
        setAudioElement(null);
        setPlayingTTS(null);
      }
    };
  }, [selectedAgent]);

  // Fonction pour parler automatiquement (auto-speak)
  const speakText = async (text) => {
    if (!text || !selectedAgent) return;
    
    setIsSpeaking(true);
    
    try {
      const token = localStorage.getItem('token');
      const voice = selectedAgent?.voice || selectedAgent?.voix || 'nova';
      
      // Limiter le texte à 2000 caractères pour l'auto-speak
      const textToSpeak = text.length > 2000 ? text.substring(0, 2000) + "..." : text;
      
      const response = await fetch(`${API_URL}/api/integrations/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-Societe-Id': localStorage.getItem('activeSocieteId') || ''
        },
        body: JSON.stringify({
          text: textToSpeak,
          voice: voice
        })
      });

      if (!response.ok) return;

      const data = await response.json();
      
      if (!data.success || !data.audio_base64) return;
      
      const audioData = atob(data.audio_base64);
      const audioArray = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioArray[i] = audioData.charCodeAt(i);
      }
      const audioBlob = new Blob([audioArray], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };
      
      audio.onerror = () => {
        setIsSpeaking(false);
      };
      
      await audio.play();
      
    } catch (error) {
      console.error('Erreur auto-speak:', error);
    } finally {
      // Timeout de sécurité
      setTimeout(() => setIsSpeaking(false), 30000);
    }
  };

  const fetchConversations = useCallback(async (agentType) => {
    try {
      const headers = getHeaders();
      delete headers['Content-Type']; // Pas besoin pour GET
      const url = searchConvQuery.trim().length >= 2
        ? `${API_URL}/api/brain/conversations/search?q=${encodeURIComponent(searchConvQuery.trim())}&agent_type=${agentType}&limit=30`
        : `${API_URL}/api/brain/conversations?agent_type=${agentType}&limit=30`;
      const response = await fetch(url, { headers });
      const data = await response.json();
      setConversations(data.conversations || []);
      
      // Charger les emails des collaborateurs
      const allSharedWith = (data.conversations || []).flatMap(c => c.shared_with || []);
      const uniqueIds = [...new Set(allSharedWith)];
      for (const userId of uniqueIds) {
        if (!collaboratorEmails[userId]) {
          fetchCollaboratorEmail(userId);
        }
      }
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
    }
  }, [getHeaders, collaboratorEmails, searchConvQuery]);

  // Sauvegarder un nouveau titre
  const saveConversationTitle = async (convId, newTitle) => {
    if (!newTitle || !newTitle.trim()) {
      setEditingTitleConvId(null);
      return;
    }
    try {
      const headers = getHeaders();
      delete headers['Content-Type'];
      await fetch(`${API_URL}/api/brain/conversations/${convId}/title?title=${encodeURIComponent(newTitle.trim())}`, {
        method: 'PUT',
        headers
      });
      // Mettre à jour localement
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, title: newTitle.trim() } : c));
      if (activeConversation?.id === convId) {
        setActiveConversation(prev => ({ ...prev, title: newTitle.trim() }));
      }
      setEditingTitleConvId(null);
      setEditingTitleValue('');
    } catch (error) {
      console.error('Erreur sauvegarde titre:', error);
    }
  };
  
  // Récupérer l'email d'un collaborateur par son ID
  const fetchCollaboratorEmail = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/brain/users/${userId}/email`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCollaboratorEmails(prev => ({ ...prev, [userId]: data }));
      }
    } catch (error) {
      console.error('Erreur récupération email:', error);
    }
  };

  const selectAgent = (agent) => {
    setSelectedAgent(agent);
    setActiveConversation(null);
    setMessages([]);
  };

  const startNewConversation = () => {
    setActiveConversation(null);
    setMessages([]);
    setAttachedFiles([]);
    setAttachedUrls([]);
  };

  // Upload de fichier
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      try {
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/api/brain/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        const data = await response.json();
        
        if (data.success) {
          setAttachedFiles(prev => [...prev, {
            id: data.file.id,
            name: file.name,
            type: file.type,
            size: file.size
          }]);
        } else {
          toast.error(`Erreur upload: ${file.name}`);
        }
      } catch (error) {
        console.error('Erreur upload:', error);
        toast.error(`Erreur upload: ${file.name}`);
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Ajouter une URL
  const addUrl = () => {
    if (urlInput.trim() && (urlInput.startsWith('http://') || urlInput.startsWith('https://'))) {
      setAttachedUrls(prev => [...prev, urlInput.trim()]);
      setUrlInput('');
      setShowUrlInput(false);
    } else {
      toast.error('URL invalide (doit commencer par http:// ou https://)');
    }
  };

  // Supprimer un fichier attaché
  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Supprimer une URL attachée
  const removeUrl = (index) => {
    setAttachedUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Icône selon type de fichier
  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return Image;
    if (type?.startsWith('video/')) return Film;
    if (type?.startsWith('audio/')) return Music;
    return FileText;
  };

  const loadConversation = async (conv) => {
    try {
      const headers = getHeaders();
      delete headers['Content-Type'];
      const response = await fetch(`${API_URL}/api/brain/conversations/${conv.id}`, {
        headers
      });
      const data = await response.json();
      setActiveConversation(data);
      setMessages(data.messages || []);
      
      // Charger les emails des collaborateurs de cette conversation
      for (const userId of (data.shared_with || [])) {
        if (!collaboratorEmails[userId]) {
          fetchCollaboratorEmail(userId);
        }
      }
    } catch (error) {
      console.error('Erreur chargement conversation:', error);
      toast.error('Erreur de chargement');
    }
  };

  const sendMessage = async (text = inputMessage) => {
    if ((!text.trim() && attachedFiles.length === 0 && attachedUrls.length === 0) || !selectedAgent) return;

    setSending(true);
    
    // Message utilisateur avec indicateurs de pièces jointes
    let userContent = text;
    if (attachedFiles.length > 0) {
      userContent += `\n📎 ${attachedFiles.length} fichier(s) attaché(s)`;
    }
    if (attachedUrls.length > 0) {
      userContent += `\n🔗 ${attachedUrls.length} lien(s)`;
    }
    
    const userMessage = { role: 'user', content: userContent, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');

    try {
      const response = await api.post('/brain/chat', {
        message: text,
        agent_type: selectedAgent.id,
        conversation_id: activeConversation?.id,
        file_ids: attachedFiles.map(f => f.id),
        urls: attachedUrls
      });

      const data = response.data;
      
      if (data.response) {
        const assistantMessage = { 
          role: 'assistant', 
          content: data.response, 
          timestamp: new Date().toISOString(),
          generated_document: data.generated_document || null,
          suggestions: data.suggestions || null
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Notification si un document a été généré
        if (data.generated_document) {
          toast.success(`Document "${data.generated_document.filename}" prêt au téléchargement !`, {
            duration: 5000,
            action: {
              label: 'Télécharger',
              onClick: () => {
                window.open(`${API_URL}${data.generated_document.download_url}`, '_blank');
              }
            }
          });
        }
        
        if (!activeConversation) {
          setActiveConversation({ id: data.conversation_id });
          fetchConversations(selectedAgent.id);
        }
        
        // Auto-speak si activé
        if (autoSpeak && voiceMode) {
          // Délai court pour laisser le message s'afficher
          setTimeout(() => {
            speakText(data.response);
          }, 300);
        }
      }
      
      // Nettoyer les pièces jointes après envoi
      setAttachedFiles([]);
      setAttachedUrls([]);
      
    } catch (error) {
      console.error('Erreur envoi message:', error);
      toast.error('Erreur de communication');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Agent selection view
  if (!selectedAgent) {
    return (
      <div className="space-y-6" data-testid="agents-page">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Équipe IA</h1>
          <p className="text-muted-foreground">
            Vos assistants spécialisés disponibles 24h/24
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {agents.filter(a => a.is_enabled !== false).map((agent) => {
            const Icon = ICONS[agent.icon] || Bot;
            return (
              <Card 
                key={agent.id}
                className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] group relative"
                onClick={() => selectAgent(agent)}
                data-testid={`agent-card-${agent.id}`}
              >
                {/* Customization button */}
                <AgentCustomizationDialog 
                  agent={agent} 
                  voices={voices}
                  onSave={saveAgentCustomization}
                  onReset={resetAgentCustomization}
                />
                
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {agent.avatar_url ? (
                      <img 
                        src={agent.avatar_url} 
                        alt={agent.nom}
                        className="w-14 h-14 rounded-full object-cover border-2 flex-shrink-0 transition-transform group-hover:scale-110"
                        style={{ borderColor: agent.color }}
                      />
                    ) : (
                      <div 
                        className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                        style={{ backgroundColor: `${agent.color}20` }}
                      >
                        <Icon className="w-7 h-7" style={{ color: agent.color }} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg group-hover:text-primary transition-colors">
                        {agent.nom}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {agent.role || agent.description}
                      </p>
                      {agent.is_customized && (
                        <Badge variant="outline" className="mt-1 text-xs">
                          Personnalisé
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-4 flex flex-wrap gap-1">
                    {agent.suggestions?.slice(0, 2).map((s, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {s.length > 25 ? s.substring(0, 25) + '...' : s}
                      </Badge>
                    ))}
                  </div>
                  
                  {/* Boutons d'action */}
                  <div className="mt-4 pt-3 border-t flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAgent(agent);
                      }}
                    >
                      <MessageSquare className="w-4 h-4" />
                      Chat écrit
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className="flex-1 gap-1.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAgent(agent);
                        setVoiceMode(true);
                      }}
                    >
                      <Mic className="w-4 h-4" />
                      Vocal
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Chat view with selected agent
  const Icon = ICONS[selectedAgent.icon] || Bot;
  
  return (
    <div className="h-[calc(100vh-8rem)] flex gap-4" data-testid="agent-chat-view">
      {/* Sidebar - Conversations */}
      <div className="w-64 flex-shrink-0 hidden md:flex flex-col">
        <Button 
          variant="outline" 
          className="mb-4"
          onClick={() => setSelectedAgent(null)}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Tous les agents
        </Button>

        <Card className="flex-1 flex flex-col">
          <CardHeader className="py-3 px-4">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-sm">Conversations</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={startNewConversation}
                data-testid="btn-new-conversation"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={searchConvQuery}
                onChange={(e) => setSearchConvQuery(e.target.value)}
                placeholder="Rechercher un sujet..."
                className="h-8 text-xs pl-7"
                data-testid="input-search-conversations"
              />
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors group ${
                    activeConversation?.id === conv.id 
                      ? 'bg-primary/10 text-primary' 
                      : 'hover:bg-muted'
                  }`}
                >
                  {editingTitleConvId === conv.id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        value={editingTitleValue}
                        onChange={(e) => setEditingTitleValue(e.target.value)}
                        onBlur={() => saveConversationTitle(conv.id, editingTitleValue)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveConversationTitle(conv.id, editingTitleValue);
                          if (e.key === 'Escape') { setEditingTitleConvId(null); setEditingTitleValue(''); }
                        }}
                        autoFocus
                        className="h-7 text-xs"
                        data-testid={`input-title-${conv.id}`}
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => loadConversation(conv)}
                      className="w-full text-left"
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate font-medium flex-1">
                          {conv.title || conv.messages?.[0]?.content?.substring(0, 30) || 'Nouvelle conversation'}
                        </span>
                        <Pencil
                          className="w-3 h-3 opacity-0 group-hover:opacity-60 hover:opacity-100 cursor-pointer flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingTitleConvId(conv.id);
                            setEditingTitleValue(conv.title || '');
                          }}
                          data-testid={`btn-edit-title-${conv.id}`}
                        />
                      </div>
                      {conv.match_snippet && (
                        <p className="text-xs text-muted-foreground italic mt-1 line-clamp-2">
                          « {conv.match_snippet} »
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs text-muted-foreground">
                          {new Date(conv.updated_at).toLocaleDateString('fr-FR')}
                        </p>
                        {conv.is_shared && (
                          <Badge variant="outline" className="text-[10px] py-0">
                            Partagé
                          </Badge>
                        )}
                        {conv.shared_with?.length > 0 && conv.is_owner && (
                          <Badge variant="secondary" className="text-[10px] py-0">
                            <Users className="w-2 h-2 mr-1" />
                            {conv.shared_with.length}
                          </Badge>
                        )}
                      </div>
                    </button>
                  )}
                </div>
              ))}
              {conversations.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {searchConvQuery.trim().length >= 2 ? 'Aucun résultat' : 'Aucune conversation'}
                </p>
              )}
            </div>
          </ScrollArea>
        </Card>
      </div>

      {/* Main Chat Area */}
      <Card className="flex-1 flex flex-col">
        {/* Header */}
        <CardHeader className="py-3 px-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm"
                className="md:hidden"
                onClick={() => setSelectedAgent(null)}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {selectedAgent.avatar_url ? (
                <img 
                  src={selectedAgent.avatar_url} 
                  alt={selectedAgent.nom}
                  className="w-10 h-10 rounded-full object-cover border-2"
                  style={{ borderColor: selectedAgent.color }}
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${selectedAgent.color}20` }}
                >
                  <Icon className="w-5 h-5" style={{ color: selectedAgent.color }} />
                </div>
              )}
              <div>
                <h2 className="font-semibold">{selectedAgent.nom}</h2>
                <p className="text-xs text-muted-foreground">{selectedAgent.role || selectedAgent.description}</p>
              </div>
            </div>
            
            {/* Boutons d'actions */}
            <div className="flex items-center gap-2">
              {/* Bouton mode vocal */}
              <Button
                variant={voiceMode ? "default" : "outline"}
                size="sm"
                onClick={() => setVoiceMode(!voiceMode)}
                className={`gap-2 ${voiceMode ? 'bg-primary' : ''}`}
                data-testid="voice-mode-btn"
                title={voiceMode ? "Désactiver le mode vocal" : "Activer le mode vocal"}
              >
                {voiceMode ? (
                  <>
                    <Volume2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Vocal ON</span>
                  </>
                ) : (
                  <>
                    <Mic className="w-4 h-4" />
                    <span className="hidden sm:inline">Mode Vocal</span>
                  </>
                )}
              </Button>
              
              {/* Indicateur si l'agent parle */}
              {isSpeaking && (
                <Badge variant="secondary" className="animate-pulse gap-1">
                  <Volume2 className="w-3 h-3" />
                  Parle...
                </Badge>
              )}
              
              {/* Bouton partage */}
              {activeConversation && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="share-conversation-btn">
                      <Users className="w-4 h-4 mr-2" />
                      Partager
                      {activeConversation.shared_with?.length > 0 && (
                        <Badge variant="secondary" className="ml-2">{activeConversation.shared_with.length}</Badge>
                      )}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Partager cette conversation</DialogTitle>
                      <DialogDescription>
                        Invitez un collaborateur du même workspace à voir cette conversation
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Email du collaborateur</Label>
                        <div className="flex gap-2">
                          <Input 
                            id="share-email"
                            type="email"
                            placeholder="collegue@exploitation.fr"
                            className="flex-1"
                          />
                          <Button 
                            data-testid="invite-collaborator-btn"
                            onClick={async () => {
                              const email = document.getElementById('share-email').value;
                              if (!email) {
                                toast.error('Entrez un email');
                                return;
                              }
                              try {
                                const headers = getHeaders();
                                const res = await fetch(`${API_URL}/api/brain/conversations/${activeConversation.id}/share?collaborator_email=${encodeURIComponent(email)}`, {
                                  method: 'POST',
                                  headers
                                });
                                const data = await res.json();
                                if (data.success) {
                                  toast.success(data.message);
                                  document.getElementById('share-email').value = '';
                                  loadConversation(activeConversation);
                                } else {
                                  toast.error(data.detail || 'Erreur de partage');
                                }
                              } catch (error) {
                                toast.error('Erreur de partage');
                              }
                            }}
                          >
                            Inviter
                          </Button>
                        </div>
                      </div>
                    
                      {activeConversation.shared_with?.length > 0 && (
                        <div className="space-y-2">
                          <Label>Collaborateurs avec accès :</Label>
                          <div className="space-y-2">
                            {activeConversation.shared_with.map((userId, i) => (
                              <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Users className="w-4 h-4 text-primary" />
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium">
                                      {collaboratorEmails[userId]?.name || collaboratorEmails[userId]?.email || `ID: ${userId.slice(0, 8)}...`}
                                    </span>
                                    {collaboratorEmails[userId]?.email && collaboratorEmails[userId]?.name && (
                                      <p className="text-xs text-muted-foreground">{collaboratorEmails[userId].email}</p>
                                    )}
                                  </div>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                  data-testid={`remove-collaborator-${i}`}
                                  onClick={async () => {
                                    const headers = getHeaders();
                                    delete headers['Content-Type'];
                                    await fetch(`${API_URL}/api/brain/conversations/${activeConversation.id}/share/${userId}`, {
                                      method: 'DELETE',
                                      headers
                                    });
                                    toast.success('Collaborateur retiré');
                                    loadConversation(activeConversation);
                                  }}
                                >
                                  <UserMinus className="w-4 h-4 mr-1" />
                                  Retirer
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </CardHeader>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4 max-w-3xl mx-auto">
            {messages.length === 0 ? (
              <div className="text-center py-8">
                {selectedAgent.avatar_url ? (
                  <img 
                    src={selectedAgent.avatar_url} 
                    alt={selectedAgent.nom}
                    className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-4"
                    style={{ borderColor: `${selectedAgent.color}40` }}
                  />
                ) : (
                  <div 
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${selectedAgent.color}15` }}
                  >
                    <Icon className="w-10 h-10" style={{ color: selectedAgent.color }} />
                  </div>
                )}
                <h3 className="font-semibold mb-2">
                  Bonjour, je suis {selectedAgent.nom}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {selectedAgent.role || selectedAgent.description}
                </p>
                
                {/* Quick suggestions */}
                <div className="flex flex-wrap justify-center gap-2">
                  {selectedAgent.suggestions?.map((suggestion, i) => (
                    <Button
                      key={i}
                      variant="outline"
                      size="sm"
                      onClick={() => sendMessage(suggestion)}
                      disabled={sending}
                      className="text-sm"
                    >
                      <Sparkles className="w-3 h-3 mr-2" />
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    }`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-2">
                          {selectedAgent.avatar_url ? (
                            <img 
                              src={selectedAgent.avatar_url} 
                              alt={selectedAgent.nom}
                              className="w-5 h-5 rounded-full object-cover"
                            />
                          ) : (
                            <Icon className="w-4 h-4" style={{ color: selectedAgent.color }} />
                          )}
                          <span className="text-xs font-medium" style={{ color: selectedAgent.color }}>
                            {selectedAgent.nom}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Bouton téléchargement document si généré */}
                          {msg.generated_document && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 text-xs gap-1"
                              onClick={() => window.open(`${API_URL}${msg.generated_document.download_url}`, '_blank')}
                              title={`Télécharger ${msg.generated_document.filename}`}
                              data-testid={`download-doc-${index}`}
                            >
                              <Download className="w-3 h-3" />
                              {msg.generated_document.filename}
                            </Button>
                          )}
                          {/* Bouton TTS */}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => playTTS(msg.content, index)}
                            title={playingTTS === index ? "Arrêter" : "Écouter"}
                            data-testid={`tts-btn-${index}`}
                          >
                            {playingTTS === index ? (
                              <Loader2 className="w-3 h-3 animate-spin" style={{ color: selectedAgent.color }} />
                            ) : (
                              <Volume2 className="w-3 h-3" style={{ color: selectedAgent.color }} />
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                    <div className="text-sm leading-relaxed">
                      <MessageRenderer content={msg.content} agentColor={selectedAgent?.color} />
                    </div>
                    {/* Suggestions dynamiques après la réponse */}
                    {msg.role === 'assistant' && msg.suggestions && msg.suggestions.length > 0 && index === messages.length - 1 && (
                      <div className="flex flex-wrap gap-1.5 mt-3 pt-2 border-t border-border/40">
                        {msg.suggestions.map((sug, si) => (
                          <Button
                            key={si}
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => sendMessage(sug)}
                            disabled={sending}
                            data-testid={`dynamic-suggestion-${si}`}
                          >
                            {sug}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
            
            {sending && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: selectedAgent.color }} />
                    <span className="text-sm text-muted-foreground">
                      {selectedAgent.nom} réfléchit...
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Pièces jointes en attente */}
        {(attachedFiles.length > 0 || attachedUrls.length > 0) && (
          <div className="px-4 pt-2 border-t bg-muted/30">
            <div className="max-w-3xl mx-auto flex flex-wrap gap-2">
              {attachedFiles.map((file, index) => {
                const FileIcon = getFileIcon(file.type);
                return (
                  <Badge key={`file-${index}`} variant="secondary" className="flex items-center gap-1 py-1 px-2">
                    <FileIcon className="w-3 h-3" />
                    <span className="text-xs max-w-[100px] truncate">{file.name}</span>
                    <button onClick={() => removeFile(index)} className="ml-1 hover:text-destructive">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                );
              })}
              {attachedUrls.map((url, index) => (
                <Badge key={`url-${index}`} variant="outline" className="flex items-center gap-1 py-1 px-2">
                  <Link className="w-3 h-3" />
                  <span className="text-xs max-w-[100px] truncate">{url}</span>
                  <button onClick={() => removeUrl(index)} className="ml-1 hover:text-destructive">
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Input avec boutons multimédia */}
        <div className="p-4 border-t">
          <div className="max-w-3xl mx-auto">
            {/* Barre d'outils multimédia */}
            <div className="flex items-center gap-2 mb-2">
              {/* Upload fichier */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                multiple
                accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || uploading}
                title="Joindre un fichier"
                data-testid="attach-file-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Paperclip className="w-4 h-4" />
                )}
                <span className="ml-1 text-xs hidden sm:inline">Fichier</span>
              </Button>

              {/* Ajouter lien */}
              {showUrlInput ? (
                <div className="flex items-center gap-1">
                  <Input
                    value={urlInput}
                    onChange={(e) => setUrlInput(e.target.value)}
                    placeholder="https://..."
                    className="h-8 w-48 text-xs"
                    onKeyPress={(e) => e.key === 'Enter' && addUrl()}
                  />
                  <Button size="sm" variant="ghost" onClick={addUrl}>
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowUrlInput(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUrlInput(true)}
                  disabled={sending}
                  title="Ajouter un lien"
                  data-testid="attach-link-btn"
                >
                  <Link className="w-4 h-4" />
                  <span className="ml-1 text-xs hidden sm:inline">Lien</span>
                </Button>
              )}

              {/* Photo rapide (mobile) */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.capture = 'environment';
                  input.onchange = (e) => handleFileUpload(e);
                  input.click();
                }}
                disabled={sending}
                className="sm:hidden"
                title="Prendre une photo"
              >
                <Camera className="w-4 h-4" />
              </Button>
            </div>

            {/* Zone de saisie principale */}
            <div className="flex gap-2 items-center">
              {/* Enregistrement vocal */}
              <VoiceRecorder 
                onTranscription={(text, fileId) => {
                  if (fileId) {
                    setAttachedFiles(prev => [...prev, { id: fileId, name: 'Message vocal', type: 'audio/webm' }]);
                  }
                  if (text && text !== "[🎙️ Message vocal]") {
                    setInputMessage(prev => prev ? `${prev} ${text}` : text);
                  }
                }}
                disabled={sending}
              />

              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={`Demander à ${selectedAgent.nom}...`}
                disabled={sending}
                className="flex-1"
                data-testid="chat-input"
              />
              <Button 
                onClick={() => sendMessage()}
                disabled={(!inputMessage.trim() && attachedFiles.length === 0 && attachedUrls.length === 0) || sending}
                style={{ backgroundColor: selectedAgent.color }}
                data-testid="send-button"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default AgentsPage;
